import { useAtomValue } from "jotai"
import { MouseEvent } from "react"
import i18n from "i18next"
import { Spinner } from "@/components/ui/Spinner"
import { ProjectListItemView, ProjectThumbnail } from "@/components/ProjectThumbnail"
import { ProjectItem } from "@/store/project/projects-service"
import {
	sampleProjectProgressAtom,
	sampleProjectStatusAtom,
	type SampleProjectStatus,
} from "@/store/project/sample-project-loader"
import clsx from "clsx"

const getStatusLabel = (status: SampleProjectStatus, percent: number) => {
	switch (status) {
		case "idle":
		case "loading":
			return i18n.t("sampleProjectThumbnail.preparingSample", { percent, ns: "components" })
		case "error":
			return i18n.t("sampleProjectThumbnail.unavailable", { ns: "components" })
		default:
			return null
	}
}

const preventInteraction = (event: MouseEvent) => {
	event.preventDefault()
	event.stopPropagation()
}

interface SampleProjectThumbnailProps {
	project: ProjectItem
	displayInfo?: boolean
}

export const SampleProjectThumbnail = (props: SampleProjectThumbnailProps) => {
	const status = useAtomValue(sampleProjectStatusAtom)
	const progress = useAtomValue(sampleProjectProgressAtom)
	const isReady = status === "ready"
	const label = getStatusLabel(status, progress?.percent ?? 0)

	return (
		<div
			className={clsx("relative", !isReady && "opacity-80")}
			aria-busy={!isReady}
			aria-disabled={!isReady}
		>
			<ProjectThumbnail {...props} />
			{!isReady && (
				<div
					className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-base-900/70 px-4 text-center"
					onClick={preventInteraction}
					onContextMenu={preventInteraction}
				>
					{(status === "loading" || status === "idle") && <Spinner size={28} />}
					{label && <div className="text-xs text-base-200">{label}</div>}
				</div>
			)}
		</div>
	)
}

interface SampleProjectListItemViewProps {
	project: ProjectItem
	layout: string[]
}

export const SampleProjectListItemView = (props: SampleProjectListItemViewProps) => {
	const status = useAtomValue(sampleProjectStatusAtom)
	const progress = useAtomValue(sampleProjectProgressAtom)
	const isReady = status === "ready"
	const label = getStatusLabel(status, progress?.percent ?? 0)

	return (
		<div
			className={clsx("relative", !isReady && "opacity-80")}
			aria-busy={!isReady}
			aria-disabled={!isReady}
		>
			<ProjectListItemView {...props} />
			{!isReady && (
				<div
					className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-base-900/70 px-4 text-center"
					onClick={preventInteraction}
					onContextMenu={preventInteraction}
				>
					{(status === "loading" || status === "idle") && <Spinner size={24} />}
					{label && <div className="text-xs text-base-200">{label}</div>}
				</div>
			)}
		</div>
	)
}
